/**
 * pre-signed URL(`/common/files/pre-signed-url`) 업로드 관련 유틸
 *
 * - 서버가 허용하는 확장자: pdf, jpg, jpeg, png, gif, zip (그 외는 발급 요청에서 400)
 * - S3 PUT 시 Content-Type은 서버가 확장자로 정한 값과 정확히 같아야 한다.
 *   (서명에 포함되므로 다르면 403 SignatureDoesNotMatch)
 */
export const ALLOWED_UPLOAD_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'gif', 'zip'] as const;

const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  pdf: 'application/pdf',
  zip: 'application/zip'
};

export const getFileExtension = (fileName: string): string => {
  const extension = fileName?.split('.').pop() ?? '';
  return extension === fileName ? '' : extension.toLowerCase();
};

/** 서버가 확장자로 판단하는 Content-Type과 동일한 값 (그 외는 application/octet-stream) */
export const getUploadContentType = (fileName: string): string =>
  CONTENT_TYPE_BY_EXTENSION[getFileExtension(fileName)] ?? 'application/octet-stream';

/** pre-signed URL 발급이 가능한 확장자인지 */
export const isAllowedUploadFileName = (fileName: string): boolean =>
  (ALLOWED_UPLOAD_EXTENSIONS as readonly string[]).includes(getFileExtension(fileName));

/** 이미지 업로드 input accept 값 (이미지 등록 API 허용 확장자) */
export const UPLOAD_IMAGE_ACCEPT = '.jpg,.jpeg,.png';

/** 이미지 등록 API가 허용하는 확장자 (그 외는 -4021) */
export const UPLOAD_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'] as const;

export const isUploadImageFileName = (fileName: string): boolean =>
  (UPLOAD_IMAGE_EXTENSIONS as readonly string[]).includes(getFileExtension(fileName));

/**
 * 크롭 결과(canvas Blob)를 올릴 파일명. 이 확장자로 인코딩 MIME과 presigned Content-Type이 함께 정해진다.
 * 확장자가 없는 기존 이미지명(재크롭)은 JPEG로 올린다.
 */
export const toUploadImageFileName = (fileName: string): string =>
  isUploadImageFileName(fileName) ? fileName : `${fileName}.jpg`;

/**
 * 이미지 업로드 사전검증(확장자) — 통과하면 null, 아니면 사용자에게 보여줄 메시지
 * 용량은 정책상 프론트에서 막지 않는다 — 서버가 -4058 메시지를 내려주면 그대로 띄운다
 */
export const getImageUploadError = (fileName: string): string | null =>
  isUploadImageFileName(fileName) ? null : 'jpg, jpeg, png 형식의 이미지만 등록할 수 있습니다.';

/** apis/errorHandler가 서버 메시지로 노출하는 HTTP 상태 */
const ERROR_HANDLER_HTTP_STATUS = [400, 401, 500];

export const DEFAULT_UPLOAD_ERROR_MESSAGE = '이미지 등록 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';

/** 업로드 흐름 에러 문구 — null이면 공통 errorHandler에 맡긴다 */
export const getUploadErrorMessage = (error): string | null => {
  const response = error?.response;
  if (response) {
    return ERROR_HANDLER_HTTP_STATUS.includes(response.status) ? null : response.data?.message || DEFAULT_UPLOAD_ERROR_MESSAGE;
  }
  // 응답 없는 네트워크 에러는 'Network Error' 원문 대신 기본 문구
  if (error?.isAxiosError) {
    return DEFAULT_UPLOAD_ERROR_MESSAGE;
  }
  return error?.message || DEFAULT_UPLOAD_ERROR_MESSAGE;
};
